import { useCallback, useMemo, type Dispatch, type SetStateAction } from "react"

import { relativePresets } from "@/constants/date-presets"
import {
  Checkbox,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@coval/ui"
import { SlidersHorizontal, X } from "lucide-react"

import { DateRangePanel } from "@/components/date-range/DateRangePanel"
import type { DatePreset } from "@/types/DatePreset"

import {
  ALL_PROJECTS_FILTER,
  type AgreementFilter,
  type OrderingFilter,
  type ProjectFilter,
  type ReviewerFilter,
  type SimulationFilters,
} from "../../utils/filterSimulations"

// Compact wording: the grid runs three across, so "Last" would not fit.
const LABEL_DATE_PRESETS = relativePresets(
  ["1d", "3d", "7d", "14d", "30d", "90d"],
  { compact: true },
)

export interface AnnotationsFilters extends SimulationFilters {
  agreement: AgreementFilter
  ordering: OrderingFilter
  reviewer: ReviewerFilter
  project: ProjectFilter
}

interface HumanReviewProjectOption {
  ulid: string
  name: string
}

interface AnnotationsFiltersPopoverProps {
  filters: AnnotationsFilters
  setFilters: Dispatch<SetStateAction<AnnotationsFilters>>
  reviewers: string[]
  relevantProjects: HumanReviewProjectOption[]
  usesRegions: boolean
  toleranceSeconds: number
  onToleranceSecondsChange: (toleranceSeconds: number) => void
  canRevealAgreement: boolean
}

export function AnnotationsFiltersPopover({
  filters,
  setFilters,
  reviewers,
  relevantProjects,
  usesRegions,
  toleranceSeconds,
  onToleranceSecondsChange,
  canRevealAgreement,
}: AnnotationsFiltersPopoverProps) {
  const labelDateRange = useMemo(
    () => ({
      start: filters.labeledAfter ? new Date(filters.labeledAfter) : null,
      end: filters.labeledBefore ? new Date(filters.labeledBefore) : null,
    }),
    [filters.labeledAfter, filters.labeledBefore],
  )
  const hasLabelDateRange = Boolean(
    filters.labeledAfter || filters.labeledBefore,
  )

  const handleLabelDateRangeChange = useCallback(
    (start: Date | null, end: Date | null) => {
      setFilters((previous) => ({
        ...previous,
        labeledAfter: start ? start.toISOString() : undefined,
        labeledBefore: end ? end.toISOString() : undefined,
      }))
    },
    [setFilters],
  )

  const handleLabelPresetClick = useCallback(
    (preset: DatePreset) => {
      const { start, end } = preset.getValue()
      setFilters((previous) => ({
        ...previous,
        labeledAfter: start.toISOString(),
        labeledBefore: end.toISOString(),
      }))
    },
    [setFilters],
  )

  const handleClearLabelDateRange = useCallback(() => {
    setFilters((previous) => ({
      ...previous,
      labeledAfter: undefined,
      labeledBefore: undefined,
    }))
  }, [setFilters])

  return (
    <Popover>
      <PopoverTrigger>
        <button
          className="w-8 h-8 flex items-center justify-center rounded-md bg-surface-1 text-coval-text-muted hover:bg-surface-1 hover:text-coval-text transition-colors"
          aria-label="Open filters"
        >
          <SlidersHorizontal className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-4 max-h-[70vh] overflow-y-auto"
        side="bottom"
        align="end"
      >
        <div className="flex flex-col gap-2">
          {canRevealAgreement && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-coval-text-muted whitespace-nowrap">
                Human vs AI Agreement
              </span>
              <Select
                value={filters.agreement}
                onValueChange={(value) =>
                  setFilters((previous) => ({
                    ...previous,
                    agreement: value as AgreementFilter,
                  }))
                }
              >
                <SelectTrigger
                  className="min-w-[130px]"
                  aria-label="Human vs AI Agreement filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="agree">Agree Only</SelectItem>
                  <SelectItem value="disagree">Disagree Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-coval-text-muted whitespace-nowrap">
              Ordering
            </span>
            <Select
              value={filters.ordering}
              onValueChange={(value) =>
                setFilters((previous) => ({
                  ...previous,
                  ordering: value as OrderingFilter,
                }))
              }
            >
              <SelectTrigger
                className="min-w-[130px]"
                aria-label="Ordering filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date_reviewed">Date Reviewed</SelectItem>
                <SelectItem value="sim_id">Simulation ID</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-coval-text-muted whitespace-nowrap">
              Reviewer
            </span>
            <Select
              value={filters.reviewer}
              onValueChange={(value) =>
                setFilters((previous) => ({
                  ...previous,
                  reviewer: value,
                }))
              }
            >
              <SelectTrigger
                className="min-w-[130px]"
                aria-label="Reviewer filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="anyone">Anyone</SelectItem>
                {reviewers.map((reviewer) => (
                  <SelectItem key={reviewer} value={reviewer}>
                    {reviewer}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-coval-text-muted whitespace-nowrap">
                Labeled
              </span>
              {hasLabelDateRange && (
                <button
                  type="button"
                  onClick={handleClearLabelDateRange}
                  className="flex items-center gap-1 text-xs text-coval-text-muted hover:text-coval-text transition-colors"
                  aria-label="Clear label date range"
                >
                  <X className="w-3 h-3" />
                  Clear
                </button>
              )}
            </div>
            <DateRangePanel
              variant="inline"
              presetColumns={3}
              value={labelDateRange}
              onRangeChange={({ start, end }) =>
                handleLabelDateRangeChange(start, end)
              }
              presets={LABEL_DATE_PRESETS}
              onPresetSelect={handleLabelPresetClick}
              presetAriaLabel={(preset) =>
                `Set label date range to ${preset.label}`
              }
              hideTimezoneHint
            />
          </div>
          {relevantProjects.length > 0 && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-coval-text-muted whitespace-nowrap">
                Human Review Project
              </span>
              <Select
                value={filters.project}
                onValueChange={(value) =>
                  setFilters((previous) => ({
                    ...previous,
                    project: value,
                  }))
                }
              >
                <SelectTrigger
                  className="min-w-[130px]"
                  aria-label="Human review project filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_PROJECTS_FILTER}>
                    All Projects
                  </SelectItem>
                  {relevantProjects.map((project) => (
                    <SelectItem key={project.ulid} value={project.ulid}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {usesRegions && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-coval-text-muted whitespace-nowrap">
                Tolerance (s)
              </span>
              <Input
                type="number"
                min={0}
                step={0.1}
                value={String(toleranceSeconds)}
                onChange={(event) => {
                  const parsed = Number.parseFloat(event.target.value)
                  if (!Number.isNaN(parsed)) {
                    if (parsed < 0) {
                      toast.warning({
                        title: "Tolerance cannot be negative",
                      })
                      return
                    }
                    onToleranceSecondsChange(parsed)
                  }
                }}
                className="min-w-[130px]"
                aria-label="Segment error tolerance in seconds"
              />
            </div>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={Boolean(filters.includeUnreviewed)}
              onCheckedChange={(value) =>
                setFilters((previous) => ({
                  ...previous,
                  includeUnreviewed: value === true,
                }))
              }
              aria-label="Include conversations with no review"
            />
            <span className="text-sm text-coval-text-muted whitespace-nowrap">
              Include unreviewed
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={filters.includeWithoutMetricOutput !== false}
              onCheckedChange={(value) =>
                setFilters((previous) => ({
                  ...previous,
                  includeWithoutMetricOutput: value === true,
                }))
              }
              aria-label="Include conversations without metric output"
            />
            <span className="text-sm text-coval-text-muted whitespace-nowrap">
              Include without metric output
            </span>
          </label>
        </div>
      </PopoverContent>
    </Popover>
  )
}
